<template>
  <v-app>
    <!-- Loading overlay for Best Alternative analysis -->
    <v-overlay
      v-if="isLoadingBestAlternative"
      :model-value="true"
      class="align-center justify-center"
      style="z-index: 9999"
    >
      <v-card class="pa-6 text-center" min-width="300">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          class="mb-4"
        />
        <h3>Beste Alternativen werden berechnet...</h3>
        <p class="mt-2 text-medium-emphasis">
          Analysiere Routen und wende Filter an
        </p>
      </v-card>
    </v-overlay>

    <!-- Error snackbar for Best Alternative issues -->
    <v-snackbar
      v-model="bestAlternativeError"
      color="error"
      timeout="6000"
      location="top"
    >
      {{ bestAlternativeErrorMessage }}
      <template #actions>
        <v-btn variant="text" @click="bestAlternativeError = false">
          Schließen
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Hint dialog shown when the selected office has no computed route data -->
    <v-dialog v-model="noRouteDataDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="text-h6">Keine Routendaten</v-card-title>
        <v-card-text>{{ noRouteDataMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="noRouteDataDialog = false">
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <the-navigation-bar
      permanent
      :mapUtilsObj="mapUtilsObj"
      :zipcodeVisible="zipcodeLegendVisible"
      @toggle-zipcode="onZipcodeToggle"
      @surcharge-toggled="changedSettingsHandler"
      @edit-surcharges="toggleSurchargeEditMode(true)"
      @show-distance-map="showMapSettings(0, 0)"
      @show-absolute-time-map="showMapSettings(1, 1)"
      @show-relative-difference-map="showMapSettings(2, 2)"
      @show-absolute-difference-map="showMapSettings(2, 3)"
      @show-best-transport-map="showMapSettings(3, 4)"
      @show-best-alternative-map="showMapSettings(4, 5)"
      @show-multiple-alternatives-map="showMapSettings(4, 6)"
    ></the-navigation-bar>
    <keep-alive>
      <component
        :is="currComponent"
        v-bind="componentProps()"
        @set-map-settings="changedSettingsHandler"
        @cancel-action="currComponent = 'jm-map'"
        @message-confirmed="currComponent = 'jm-map'"
        @no-route-data="showNoRouteDataDialog"
      >
      </component>
    </keep-alive>
    <geo-package-layer-manager
      v-if="geoPackageLayers.length > 0"
      :layers="geoPackageLayers"
      @toggle-layer="toggleGeoPackageLayer"
      @remove-layer="removeGeoPackageLayer"
      @update-layer-style="updateGeoPackageLayerStyle"
    ></geo-package-layer-manager>

    <!-- Bedienfeld für die PLZ-Auswahl. Liegt über der Karte, damit Auswahl
         und Aktionen sichtbar bleiben, während man auf Gebiete klickt. -->
    <v-card v-if="surchargeEditMode" class="surcharge-panel" elevation="8">
      <v-card-title class="text-subtitle-1 pb-1">
        Stauaufschläge bearbeiten
      </v-card-title>
      <v-card-text class="pb-2">
        <p class="text-body-2 mb-2">
          <strong>{{ selectedPlz.length }}</strong>
          {{ selectedPlz.length === 1 ? "Gebiet" : "Gebiete" }} ausgewählt
        </p>
        <p class="text-caption text-medium-emphasis mb-3">
          PLZ-Gebiete anklicken oder mit einem Polygon einrahmen, dann den
          Aufschlag einmal für alle vergeben.
        </p>

        <v-alert
          v-if="surchargeSaveError"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-2"
          closable
          @click:close="surchargeSaveError = ''"
        >
          {{ surchargeSaveError }}
        </v-alert>

        <div class="d-flex flex-column ga-2">
          <v-btn
            :color="polygonDrawActive ? 'warning' : 'primary'"
            variant="tonal"
            size="small"
            prepend-icon="mdi-vector-polygon"
            @click="polygonDrawActive ? stopPolygonDraw() : startPolygonDraw()"
          >
            {{ polygonDrawActive ? "Zeichnen abbrechen" : "Polygon zeichnen" }}
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            size="small"
            :disabled="selectedPlz.length === 0"
            prepend-icon="mdi-traffic-light"
            @click="surchargeDialogOpen = true"
          >
            Aufschlag vergeben
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            :disabled="selectedPlz.length === 0"
            @click="clearPlzSelection"
          >
            Auswahl leeren
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-check"
            @click="toggleSurchargeEditMode(false)"
          >
            Bearbeitung beenden
          </v-btn>
        </div>
      </v-card-text>

      <!-- Farbskala der Aufschlagshöhe; getrennt von der Mitarbeiterdichte -->
      <v-divider></v-divider>
      <v-card-text class="py-2">
        <p class="text-caption font-weight-medium mb-1">Aufschlagshöhe</p>
        <div
          v-for="step in surchargeColorSteps"
          :key="step.key"
          class="d-flex align-center text-caption"
        >
          <span
            class="surcharge-swatch"
            :style="{ background: step.color }"
          ></span>
          {{ step.label }}
        </div>
      </v-card-text>
    </v-card>

    <plz-surcharge-dialog
      v-model="surchargeDialogOpen"
      :plz-list="selectedPlz"
      :transports="surchargeTransports"
      :configured-plz="configuredPlz"
      :saving="surchargeSaving"
      @save="saveSurchargeForSelection"
    ></plz-surcharge-dialog>
  </v-app>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
// Zeichenwerkzeug für die PLZ-Auswahl. Free-Variante (MIT); die Bedienung
// läuft über die eigene Schaltfläche, nicht über die Geoman-Werkzeugleiste.
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import { mapStores } from "pinia";
import { useMapInputStore } from "../store/mapInputStore";
import { useMapOutputStore } from "../store/mapOutputStore";
import { useGeoPackageStore } from "../store/geoPackageStore";
import { usePlzSurchargeStore } from "../store/plzSurchargeStore";
import { useOfficeStore } from "../store/officeStore";
import PlzSurchargeDialog from "./PlzSurchargeDialog.vue";
import {
  addPlz,
  plzTouchedByPolygon,
  surchargeColor,
  SURCHARGE_COLOR_STEPS,
  togglePlz,
} from "../utils/plzSelection";
import JmMap from "./TheMap.vue";
import TheNavigationBar from "./TheNavbar.vue";
import TheMapSettings from "./TheMapSettings.vue";
import GeoPackageLayerManager from "./GeoPackageLayerManager.vue";
import cleverReachLogo from "@/assets/cleverReachLogo.png";
import officeIcon from "@/assets/Markers/officeIcon.png";
import { MapSettingType } from "../model/mapModels";
import { MapType } from "../model/mapModels";
import { storeToRefs } from "pinia";
import { materialIcon, transportSymbolColor } from "@/config/iconConfig";
import { simplifyGeoJson } from "../utils/geo-utils";
import { isNoRouteDataError } from "../utils/errors";
import { eventBus } from "../utils/eventBus";
import { MapUtils } from "../store/mapStoreComposable";
import { GeoPackageStyle } from "../utils/geopackage-style-utils";
import { getTransportDisplayName as legendTransportName } from "../utils/map";

/**
 * Beschriftung fuer die Ueberschriften der Entfernungs- und
 * Vergleichskarten.
 *
 * Bewusst nicht die Kartenbeschriftung aus utils/map: hier steht der Name aus
 * der Auswahlliste ("Park & Ride"), auf den die Abkuerzungen weiter unten
 * ("P+R") aufsetzen. Diese Karten zeigen kein Verkehrsmittel im Popup, ein
 * Abgleich mit der Legende ist dort also nicht noetig.
 */
const getTransportDisplayName = (transport: string): string => {
  return transport === "Auto" ? "Pkw" : transport;
};
const mapTypeMultipleAlternatives: MapType = 6 as MapType;

export default defineComponent({
  components: {
    JmMap,
    TheNavigationBar,
    TheMapSettings,
    GeoPackageLayerManager,
    PlzSurchargeDialog,
  },
  data() {
    return {
      mapUtilsObj: new MapUtils(),
      components: [],
      originalZipcodeData: null as GeoJSON.FeatureCollection | null,
      simplifiedZipcodeData: {} as Record<number, GeoJSON.FeatureCollection>, // Indexed by zoom level
      employeeCountsByZipcode: {} as Record<string, number>,
      lastSimplifiedZoom: null as number | null,
      zipcodeLayer: null as L.GeoJSON | null,
      zipcodeLegendVisible: false,
      noRouteDataDialog: false as boolean,
      noRouteDataMessage:
        "Für diesen Standort liegen (noch) keine berechneten Routendaten vor.",
      TipCircle: null,
      blur: false as boolean,
      currComponent: "jm-map" as string,
      showSettings: false as boolean,
      lastChosenMap: undefined as undefined | MapType,
      previousSettingsType: null as null | MapSettingType,
      geoPackageLayers: [] as Array<{
        id: string;
        name: string;
        visible: boolean;
        style?: GeoPackageStyle;
        geometryType: string;
      }>,
      // Stauaufschläge: Bearbeitungsmodus und Auswahl von PLZ-Gebieten
      surchargeEditMode: false as boolean,
      selectedPlz: [] as string[],
      polygonDrawActive: false as boolean,
      surchargeDialogOpen: false as boolean,
      surchargeSaving: false as boolean,
      surchargeSaveError: "" as string,
      polygonHandlerBound: false as boolean,
      // Sichtbarkeit des PLZ-Layers vor dem Betreten der Bearbeitung
      zipcodeVisibleBeforeEdit: false as boolean,
      // Nachschlagewerke, die nur waehrend eines setStyle-Durchlaufs leben
      styleSelectionCache: null as Set<string> | null,
      styleMaxByPlzCache: null as Record<
        string,
        { pct: number; minutes: number }
      > | null,
      // Einfärbung nach Mitarbeiterdichte; wird beim Aufbau des Layers gefüllt
      // und beim Umschalten der Darstellung wiederverwendet.
      zipcodeColorCategories: {} as Record<string, string>,
    };
  },
  computed: {
    /**
     * Zulässige Verkehrsmittel für den Aufschlag, wie das Backend sie meldet.
     *
     * Der Store wird hier direkt geholt statt über mapStores(): das steht in
     * dieser Datei im methods-Block und liefert dort Funktionen statt Stores.
     */
    surchargeTransports() {
      return usePlzSurchargeStore().transports;
    },
    /** Postleitzahlen, für die bereits ein Aufschlag gepflegt ist. */
    configuredPlz(): string[] {
      return usePlzSurchargeStore().configuredPlz;
    },
    surchargeColorSteps() {
      return SURCHARGE_COLOR_STEPS;
    },
  },
  setup() {
    const mapOutputStore = useMapOutputStore();
    const { zipcodeLayerData } = storeToRefs(mapOutputStore);

    const mapInputStore = useMapInputStore();
    const { getMetric } = storeToRefs(mapInputStore);

    // Add BestAlternative state refs from store
    const {
      isLoadingBestAlternative,
      bestAlternativeError,
      bestAlternativeErrorMessage,
    } = storeToRefs(mapOutputStore);

    return {
      mapInputStore,
      mapOutputStore,
      zipcodeLayerData,
      getMetric,
      // Add BestAlternative state to return
      isLoadingBestAlternative,
      bestAlternativeError,
      bestAlternativeErrorMessage,
    };
  },
  mounted() {
    this.components = [
      {
        tag: "jm-map",
        props: {
          mapUtilsObj: this.mapUtilsObj,
          additionalLayerGroups: this.mapUtilsObj.additionalLayerGroups,
          //storeToRefs(useMapStore()).additionalLayerGroups,
          style: "height: 100%;margin-left: 50px;",
        },
      },
      {
        tag: "the-map-settings",
        props: {
          settingsType: MapSettingType.Standard,
        },
      },
    ] as any;
    // Initialize with any existing GeoPackage layers
    this.updateGeoPackageLayersList();

    // Listen for GeoPackage layer imports
    eventBus.on("geopackage:layers-imported", () => {
      this.updateGeoPackageLayersList();
    });

    // Listen for map zoom events
    eventBus.on("map:zoom", (zoomLevel) => {
      if (this.zipcodeLegendVisible && this.zipcodeLayer) {
        this.resimplifyZipcodeLayer(zoomLevel);
      }
    });
  },
  beforeUnmount() {
    // Remove the event listener when the component is destroyed
    eventBus.off("map:zoom");
  },
  methods: {
    componentProps() {
      let tagName = this.currComponent;
      // @ts-ignore
      let index: number | undefined = this.components.findIndex((a) => {
        return a.tag === tagName;
      });
      if (index !== -1) {
        // @ts-ignore
        return this.components[index!].props;
      }
      return {};
    },
    ...mapStores(
      // useMapStore,
      useMapInputStore,
      useMapOutputStore,
      useGeoPackageStore,
    ),
    async showMapSettings(type: MapSettingType, mapType: MapType) {
      // @ts-ignore
      this.components[1].props.settingsType = type;

      if (
        type !== this.previousSettingsType ||
        mapType !== this.lastChosenMap
      ) {
        if (this.mapUtilsObj) {
          for (let layerName of ["employees", "office", "zipcode"]) {
            this.mapUtilsObj.removeLegend();
            this.mapUtilsObj.removeLayerGroup(layerName);
          }
        }
      }

      // Update previousSettingsType and lastChosenMap
      this.previousSettingsType = type;
      this.lastChosenMap = mapType;

      // Show the settings
      this.currComponent = "the-map-settings";
    },
    async showMap(mapType: MapType) {
      try {
        this.mapUtilsObj.setChosenMapType(mapType);

        // Clear existing layers first
        for (let layerName of ["employees", "office", "zipcode"]) {
          this.mapUtilsObj.removeLegend();
          this.mapUtilsObj.removeLayerGroup(layerName);
        }

        // Wait for clearing to complete
        await new Promise((resolve) => setTimeout(resolve, 100));

        let employeesLayer: L.LayerGroup | undefined;
        let officeLayer: L.LayerGroup = this.mapOutputStore.getOfficeLayer!;

        // Get the appropriate layer based on map type
        switch (mapType) {
          case MapType.Distance:
            employeesLayer = await this.mapOutputStore.getDistanceLayer;
            break;
          case MapType.AbsoluteTime:
            employeesLayer = await this.mapOutputStore.getAbsoluteTimeLayer;
            break;
          case MapType.RelativeDifference:
            employeesLayer =
              await this.mapOutputStore.getRelativeDifferenceLayer;
            break;
          case MapType.AbsoluteDifference:
            employeesLayer =
              await this.mapOutputStore.getAbsoluteDifferenceLayer;
            break;
          case MapType.BestTransport:
            employeesLayer = await this.mapOutputStore.getBestTransportLayer;
            break;
          case MapType.BestAlternative:
            employeesLayer = await this.mapOutputStore.getBestAlternativeLayer;
            break;
          case mapTypeMultipleAlternatives:
            employeesLayer =
              await this.mapOutputStore.getMultipleAlternativesLayer;
            break;
        }

        if (!officeLayer || !employeesLayer) {
          throw new Error(`Layers not found for map type ${mapType}`);
        }

        // Add circles to office layer
        let markerLatLng: L.LatLng | undefined;
        let officeMarker: L.Marker | undefined;

        officeLayer.eachLayer((layer) => {
          if (layer instanceof L.Marker) {
            officeMarker = layer as L.Marker;
            markerLatLng = officeMarker.getLatLng();
          }
        });

        if (markerLatLng && officeMarker) {
          let officeAndCirclesLayer = L.layerGroup([officeMarker]);

          const radii = [10000, 20000, 30000, 40000, 50000];
          radii.forEach((radius) => {
            const circle = this.createTipCircle(markerLatLng!, {
              color: "black",
              fill: false,
              weight: 1,
              radius: radius,
            });
            officeAndCirclesLayer.addLayer(circle);
          });

          officeLayer.addLayer(officeAndCirclesLayer);
        }

        // Add new layers with a delay between them
        await this.mapUtilsObj.addLayerGroup("office", officeLayer);
        await new Promise((resolve) => setTimeout(resolve, 100));
        await this.mapUtilsObj.addLayerGroup("employees", employeesLayer);

        // Handle zipcode layer
        try {
          await this.mapOutputStore.prepareZipcodeLayer();
          const zipcodeLayerData = this.mapOutputStore.getZipcodeLayerData;
          if (zipcodeLayerData) {
            await this.createZipcodeLayer(zipcodeLayerData);
          }
        } catch (error) {
          console.error("Error preparing zipcode layer:", error);
        }

        // Stauaufschlaege des Standorts laden. Muss vor createLegend()
        // passieren - die Legende blendet den Schalter nur ein, wenn welche
        // gepflegt sind. Ein Fehler darf die Karte nicht verhindern: dann
        // fehlt lediglich der Schalter.
        try {
          const officeId = useOfficeStore().selectedOffice?.office_id;
          if (officeId) {
            await usePlzSurchargeStore().fetchSurcharges(officeId);
          }
        } catch (error) {
          console.error("Error loading PLZ surcharges:", error);
        }

        // Create and add the legend
        const legend = this.createLegend();
        this.mapUtilsObj.addLegend(legend);
      } catch (error) {
        // No computed route data for this office: show a hint dialog instead of
        // an empty map. Layers were cleared and none were added before the throw.
        if (isNoRouteDataError(error)) {
          this.showNoRouteDataDialog();
          return;
        }
        console.error("Error showing map:", error);
      }
    },
    showNoRouteDataDialog() {
      this.noRouteDataDialog = true;
    },
    calculateEmployeeCounts() {
      const addresses = this.mapInputStore.getAdresses;
      if (!addresses) return;

      this.employeeCountsByZipcode = addresses.reduce(
        (acc, address) => {
          const zipcode = address.postal_code;
          acc[zipcode] = (acc[zipcode] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );
    },
    calculateColorCategories(): Record<string, string> {
      const sortedZipcodes = Object.keys(this.employeeCountsByZipcode).sort(
        (a, b) =>
          this.employeeCountsByZipcode[b] - this.employeeCountsByZipcode[a],
      );
      const totalZipcodes = sortedZipcodes.length;

      const darkestCount = Math.round(totalZipcodes * 0.1);
      const darkCount = Math.round(totalZipcodes * 0.25);
      const mediumCount = Math.round(totalZipcodes * 0.25);
      // The light category will be the remainder

      return sortedZipcodes.reduce(
        (acc, zipcode, index) => {
          if (index < darkestCount) acc[zipcode] = "darkest";
          else if (index < darkestCount + darkCount) acc[zipcode] = "dark";
          else if (index < darkestCount + darkCount + mediumCount)
            acc[zipcode] = "medium";
          else acc[zipcode] = "light";
          return acc;
        },
        {} as Record<string, string>,
      );
    },
    getEmployeeCountForZipcode(zipcode: string): number {
      return this.employeeCountsByZipcode[zipcode] || 0;
    },
    changedSettingsHandler() {
      if (this.lastChosenMap === undefined) {
        console.warn("Showing map not successful, no map chosen!");
        return;
      }
      // console.log("changedSettingsHandler is called.");
      //console.log("lastChosenMap: ", this.lastChosenMap);
      this.showMap(this.lastChosenMap);
      this.currComponent = "jm-map";
    },
    // Erstellen einer Funktion, die eine Instanz von TipCircle zurückgibt
    createTipCircle(latlng: L.LatLngExpression, options: L.CircleOptions) {
      type TipCircle = L.Circle & {
        _tip: L.CircleMarker;
        _setTip: () => void;
      };

      const circle = L.circle(latlng, options) as TipCircle;
      circle._tip = L.circleMarker([0, 0], { opacity: 0, radius: 0 });

      circle._setTip = function () {
        const centerLat = this.getLatLng().lat;
        const centerLng = this.getLatLng().lng;
        const radiusInMeters = this.getRadius();

        // Calculate the position at 270 degrees (left side of the circle)
        const earthRadius = 6371000; // Earth's radius in meters
        const latOffset = (radiusInMeters / earthRadius) * (180 / Math.PI);

        const tooltipLat = centerLat;
        const tooltipLng =
          centerLng - latOffset / Math.cos((centerLat * Math.PI) / 180);

        this._tip.setLatLng([tooltipLat, tooltipLng]);

        const tipText = (radiusInMeters / 1000).toFixed(0) + " km";
        this._tip.unbindTooltip();
        this._tip.bindTooltip(tipText, {
          direction: "right",
          permanent: true,
          className: "circleTip no-arrow",
          offset: [-32, 0],
        });
      };

      const originalRedraw = circle.redraw.bind(circle);
      circle.redraw = function () {
        originalRedraw();
        this._setTip();
        return this;
      };

      const originalOnAdd = circle.onAdd.bind(circle);
      circle.onAdd = function (map: L.Map) {
        originalOnAdd(map);
        this._setTip();
        this._tip.addTo(map);
        return this;
      };

      const originalOnRemove = circle.onRemove.bind(circle);
      circle.onRemove = function (map: L.Map) {
        this._tip.remove();
        originalOnRemove(map);
        return this;
      };

      return circle;
    },
    createLegend() {
      let legend = new L.Control({ position: "topright" });
      legend.onAdd = () => {
        var container = L.DomUtil.create("div", "legend");

        var img1 = L.DomUtil.create("img");
        img1.src = cleverReachLogo;
        img1.style.width = "50px";
        container.appendChild(img1);

        let transport: string | null = null;
        if (
          this.lastChosenMap !== undefined &&
          (MapType[this.lastChosenMap] === "Distance" ||
            MapType[this.lastChosenMap] === "AbsoluteTime")
        ) {
          transport = this.mapInputStore.getFirstTransport;
        }

        if (
          this.lastChosenMap !== undefined &&
          MapType[this.lastChosenMap] === "Distance"
        ) {
          var standort = L.DomUtil.create("div");
          standort.style.display = "flex";

          var img2 = L.DomUtil.create("img");
          img2.src = officeIcon;
          img2.style.width = "20px";
          standort.appendChild(img2);

          var div1 = L.DomUtil.create("div");
          div1.innerHTML = "<h4> - Standort</h4>";
          standort.appendChild(div1);

          var div2 = L.DomUtil.create("div");
          div2.innerHTML = "<h4>Fahrstrecke in km</h4>";
          div2.innerHTML += `<h4>für ${getTransportDisplayName(transport)}</h4>`;
          div2.innerHTML +=
            '<i style="background: rgb(246, 211, 135)"></i> 0 bis 10 km<br>';
          div2.innerHTML +=
            '<i style="background: rgb(226, 185, 142)"></i> 10 bis 20 km<br>';
          div2.innerHTML +=
            '<i style="background: rgb(206, 158, 149)"></i> 20 bis 30 km<br>';
          div2.innerHTML +=
            '<i style="background: rgb(186, 132, 156)"></i> 30 bis 40 km<br>';
          div2.innerHTML +=
            '<i style="background: rgb(166, 105, 163)"></i> 40 bis 50 km<br>';
          div2.innerHTML +=
            '<i style="background: rgb(146, 79, 170)"></i> 50 bis 60 km<br>';
          div2.innerHTML +=
            '<i style="background: rgb(126, 52, 177)"></i> 60 bis 70 km<br>';
          div2.innerHTML +=
            '<i style="background: rgb(106, 26, 184)"></i> 70 bis 80 km<br>';
          div2.innerHTML +=
            '<i style="background: rgb(86, 0, 191)"></i> 80 km und weiter<br>';
          container.appendChild(standort);
          container.appendChild(div2);
        } else if (
          this.lastChosenMap !== undefined &&
          MapType[this.lastChosenMap] === "AbsoluteTime"
        ) {
          standort = L.DomUtil.create("div");
          standort.style.display = "flex";

          img2 = L.DomUtil.create("img");
          img2.src = officeIcon;
          img2.style.width = "20px";
          standort.appendChild(img2);

          div1 = L.DomUtil.create("div");
          div1.innerHTML = "<h4> - Standort</h4>";
          standort.appendChild(div1);

          div2 = L.DomUtil.create("div");
          div2.innerHTML = "<h4>Absolute Fahrzeit</h4>";
          div2.innerHTML += `<h4>für ${getTransportDisplayName(transport)}</h4>`;
          div2.innerHTML +=
            '<i style="background: rgb(0, 66, 0)"></i> bis 10 min<br>';
          div2.innerHTML +=
            '<i style="background: rgb(0, 171, 74)"></i> bis 20 min<br>';
          div2.innerHTML +=
            '<i style="background: rgb(0, 194, 15)"></i> bis 30 min<br>';
          div2.innerHTML +=
            '<i style="background: rgb(255, 215, 0)"></i> bis 40 min<br>';
          div2.innerHTML +=
            '<i style="background: rgb(255, 127, 0)"></i> bis 50 min<br>';
          div2.innerHTML +=
            '<i style="background: rgb(221, 26, 32)"></i> bis 60 min<br>';
          div2.innerHTML +=
            '<i style="background: rgb(167, 0, 0)"></i> über 60 min<br>';
          container.appendChild(standort);
          container.appendChild(div2);
        } else if (
          this.lastChosenMap !== undefined &&
          MapType[this.lastChosenMap] === "RelativeDifference"
        ) {
          const metricTypeTranslation = {
            time_in_mins: "Zeit",
            co2: "CO₂",
            cost: "Kosten",
            sport: "Bewegung",
            distance: "Entfernung",
          };

          const metricType = this.getMetric;
          const transport1 = this.mapInputStore.getFirstTransport;
          const transport2 = this.mapInputStore.getSecondTransport;

          const abbreviateTransport = (transport: string) => {
            return getTransportDisplayName(transport)
              .replace("Park & Ride", "P+R")
              .replace("Bike & Ride", "B+R")
              .replace("e-Scooter & Ride", "S+R")
              .replace("ÖPV & Taxi", "ÖPV+T");
          };

          const heading = `${metricTypeTranslation[metricType] || metricType}vergleich`;
          const subheading = `${abbreviateTransport(transport1)} gegenüber ${abbreviateTransport(transport2)}`;

          container.innerHTML = `<h4>${heading}</h4>`;
          container.innerHTML += `<h4>${subheading}</h4>`;
          container.innerHTML +=
            '<i style="background: rgb(1, 66, 1)"></i> >100% schneller/besser<br>';
          container.innerHTML +=
            '<i style="background: rgb(34, 139, 34)"></i> bis 100%<br>';
          container.innerHTML +=
            '<i style="background: rgb(50, 205, 50)"></i> bis 75%<br>';
          container.innerHTML +=
            '<i style="background: rgb(144, 238, 144)"></i> bis 50%<br>';
          container.innerHTML +=
            '<i style="background: rgb(199, 240, 173)"></i> bis 25%<br>';
          container.innerHTML +=
            '<i style="background: rgb(255, 255, 255)"></i> +/- 5%<br>';
          container.innerHTML +=
            '<i style="background: rgb(255, 217, 0)"></i> bis 25%<br>';
          container.innerHTML +=
            '<i style="background: rgb(255, 166, 0)"></i> bis 50%<br>';
          container.innerHTML +=
            '<i style="background: rgb(212, 115, 30)"></i> bis 75%<br>';
          container.innerHTML +=
            '<i style="background: rgb(255, 68, 0)"></i> bis 100%<br>';
          container.innerHTML +=
            '<i style="background: rgb(139, 0, 0)"></i> >100% langsamer/schlechter<br>';
        } else if (
          this.lastChosenMap !== undefined &&
          MapType[this.lastChosenMap] === "AbsoluteDifference"
        ) {
          const metricTypeTranslation = {
            time_in_mins: "Zeit",
            co2: "CO₂",
            cost: "Kosten",
            sport: "Bewegung",
            distance: "Entfernung",
          };

          const metricUnits = {
            time_in_mins: "min",
            co2: "g",
            cost: "€",
            sport: "Schritte",
            distance: "km",
          };

          const metricRanges = {
            time_in_mins: [-60, -45, -30, -15, -5, 5, 15, 30, 45, 60],
            co2: [-1000, -750, -500, -250, -50, 50, 250, 500, 750, 1000],
            cost: [-10, -7.5, -5, -2.5, -0.5, 0.5, 2.5, 5, 7.5, 10],
            sport: [1000, 750, 500, 250, 50, -50, -250, -500, -750, -1000],
            distance: [-50, -37.5, -25, -12.5, -2.5, 2.5, 12.5, 25, 37.5, 50],
          };

          const formatNumber = (num: number) => {
            return Math.abs(num) >= 1000
              ? num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
              : num.toString();
          };

          const metricType = this.getMetric;
          const transport1 = this.mapInputStore.getFirstTransport;
          const transport2 = this.mapInputStore.getSecondTransport;
          const unit = metricUnits[metricType];
          const ranges = metricRanges[metricType];

          const isInvertedMetric = metricType === "sport";
          const lessThanSymbol = isInvertedMetric ? ">" : "<";
          const greaterThanSymbol = isInvertedMetric ? "<" : ">";

          const abbreviateTransport = (transport: string) => {
            return getTransportDisplayName(transport)
              .replace("Park & Ride", "P+R")
              .replace("Bike & Ride", "B+R")
              .replace("e-Scooter & Ride", "S+R")
              .replace("ÖPV & Taxi", "ÖPV+T");
          };

          const heading = `${metricTypeTranslation[metricType] || metricType}vergleich`;
          const subheading = `${abbreviateTransport(transport1)} gegenüber ${abbreviateTransport(transport2)}`;

          container.innerHTML = `<h4>${heading}</h4>`;
          container.innerHTML += `<h4>${subheading}</h4>`;
          container.innerHTML += `<i style="background: #006400"></i> ${lessThanSymbol} ${formatNumber(ranges[0])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #228B22"></i> ${formatNumber(ranges[0])} bis ${formatNumber(ranges[1])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #32CD32"></i> ${formatNumber(ranges[1])} bis ${formatNumber(ranges[2])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #90EE90"></i> ${formatNumber(ranges[2])} bis ${formatNumber(ranges[3])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #98FB98"></i> ${formatNumber(ranges[3])} bis ${formatNumber(ranges[4])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #FFFFFF"></i> ${formatNumber(ranges[4])} bis ${formatNumber(ranges[5])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #FFFFE0"></i> ${formatNumber(ranges[5])} bis ${formatNumber(ranges[6])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #FFD700"></i> ${formatNumber(ranges[6])} bis ${formatNumber(ranges[7])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #FFA500"></i> ${formatNumber(ranges[7])} bis ${formatNumber(ranges[8])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #FF4500"></i> ${formatNumber(ranges[8])} bis ${formatNumber(ranges[9])} ${unit}<br>`;
          container.innerHTML += `<i style="background: #8B0000"></i> ${greaterThanSymbol} ${formatNumber(ranges[9])} ${unit}<br>`;
        } else if (
          this.lastChosenMap !== undefined &&
          MapType[this.lastChosenMap] === "BestTransport"
        ) {
          const metricTypeTranslation = {
            time_in_mins: "Zeit",
            co2: "CO₂-Ausstoß",
            cost: "Kosten",
            sport: "Bewegung",
            distance: "Gesamt-Nutzen",
          };
          const metricType = this.getMetric;

          const metricLabel = metricTypeTranslation[metricType] || metricType;
          if (metricLabel === "Gesamt-Nutzen") {
            container.innerHTML += `<h3>Nutzenoptimales</h3>`;
          } else {
            container.innerHTML += `<h3>Effektivstes</h3>`;
          }
          container.innerHTML += `<h3>Verkehrsmittel</h3>`;
          container.innerHTML += `<h3>(für ${metricLabel})</h3>`;

          const transportTypes = [
            { name: "Fußweg", icon: "directions_walk", configKey: "Fuss_Weg" },
            { name: "Fahrrad", icon: "directions_bike", configKey: "Fahrrad" },
            { name: "Pedelec", icon: "electric_bike", configKey: "Pedelec" },
            { name: "ÖV", icon: "directions_bus", configKey: "OeV" },
            {
              name: "Bike+Ride",
              icon: "pedal_bike",
              configKey: "OeV_Fahrrad",
            },
            { name: "Pkw", icon: "directions_car", configKey: "PKW" },
            { name: "E-Pkw", icon: "electric_car", configKey: "E_Auto" },
            { name: "Park+Ride", icon: "commute", configKey: "Auto_OeV" },
            {
              name: "Werksbus",
              icon: "airport_shuttle",
              configKey: "Werksbus",
            },
            {
              name: "Bike+Werksbus",
              icon: "airport_shuttle",
              configKey: "Fahrrad_Werksbus",
            },
            {
              name: "Bahnreaktivierung",
              icon: "directions_bus",
              configKey: "Bahnreakt_Ride",
            },
            {
              name: "Park+Ride+Bike",
              icon: "commute",
              configKey: "Park_Ride_and_Bike",
            },
          ];

          transportTypes.forEach((transport) => {
            const defaultColor = materialIcon.fillColor[transport.configKey];
            if (transport.name === "Bahnreaktivierung") {
              // Train replacement service with composite icon
              const brColor =
                materialIcon.fillColor[transport.configKey] || "#10DADE";
              container.innerHTML += `
                <div class="legend-item" style="position: relative; display: flex; align-items: center; margin: 2px 0;">
                  <span class="icon-combo">
                    <span class="material-icons base" style="color: ${brColor};">directions_bus</span>
                    <span class="material-icons overlay" style="color: ${brColor};">train</span>
                  </span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            } else if (transport.name === "Park+Ride+Bike") {
              // Composite: commute base (depicts car + train) + bike overlay.
              // display:flex statt inline-flex, sonst rutscht der Eintrag neben
              // den vorhergehenden Bahnreaktivierung-Eintrag in dieselbe Zeile.
              const parkRideBikeColor =
                materialIcon.fillColor[transport.configKey] || "#ffd000";
              container.innerHTML += `
                <div class="legend-item" style="position: relative; display: flex; align-items: center; margin: 2px 0;">
                  <span class="icon-combo">
                    <span class="material-icons base" style="color: ${parkRideBikeColor};">commute</span>
                    <span class="material-icons overlay" style="color: ${parkRideBikeColor};">directions_bike</span>
                  </span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            } else if (transport.name === "Bike+Ride") {
              // Force Bike+Ride color to #025b29
              const bikeRideColor =
                materialIcon.fillColor[transport.configKey] || "#025b29";
              container.innerHTML += `
                <div class="legend-item" style="position: relative; display: inline-flex; align-items: center;">
                  <span class="icon-combo">
                    <span class="material-icons base" style="color: ${bikeRideColor};">directions_bus</span>
                    <span class="material-icons overlay" style="color: ${bikeRideColor};">directions_bike</span>
                  </span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            } else if (transport.name === "Bike+Werksbus") {
              // Farbe aus iconConfig, damit Legende und Marker nicht auseinanderlaufen
              const bikeWerksbusColor =
                materialIcon.fillColor[transport.configKey] || "#5b6480";
              container.innerHTML += `
                <div class="legend-item" style="position: relative; display: flex; align-items: center; margin: 2px 0;">
                  <span class="icon-combo">
                    <span class="material-icons base" style="color: ${bikeWerksbusColor};">airport_shuttle</span>
                    <span class="material-icons overlay" style="color: ${bikeWerksbusColor};">directions_bike</span>
                  </span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            } else if (
              transport.configKey === "Werksbus" &&
              defaultColor.includes("gradient")
            ) {
              // Handle Werksbus gradient color with circular background
              container.innerHTML += `
                <div class="legend-item" style="display: inline-flex; align-items: center;">
                  <div class="legend-gradient-icon" style="
                    background: ${defaultColor};
                    border: 2px solid #CC5500;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-right: 5px;
                  ">
                    <span class="material-icons" style="font-size: 16px; color: white;">${transport.icon}</span>
                  </div>
                  <span style="vertical-align: middle;">${transport.name}</span>
                </div>
              `;
            } else {
              container.innerHTML += `
                <div class="legend-item">
                  <span class="material-icons" style="vertical-align: middle; color: ${defaultColor};">${transport.icon}</span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            }
          });
        } else if (
          this.lastChosenMap !== undefined &&
          MapType[this.lastChosenMap] === "BestAlternative"
        ) {
          const metricTypeTranslation = {
            time: "Zeit",
            co2: "CO₂-Ausstoß",
            cost: "Kosten",
            sport: "Bewegung",
            distance: "Gesamt-Nutzen",
          };
          const filters = this.mapInputStore.getBestAlternativeFilters;
          const metricLabel =
            metricTypeTranslation[filters.selected_metric] ||
            filters.selected_metric;

          // Header - same as BestTransport but with "Alternative" context
          container.innerHTML += `<h3>Beste Alternative</h3>`;
          container.innerHTML += `<h3>zu ${legendTransportName(filters.current_transport)}</h3>`;
          container.innerHTML += `<h3>(für ${metricLabel})</h3>`;
          // Filter criteria information
          container.innerHTML += `<div style="margin: 5px 0; padding: 4px; background-color: #f5f5f5; border-radius: 4px; font-size: 11px;">`;
          container.innerHTML += `<strong>Kriterien:</strong><br>`;
          container.innerHTML += `Max. +${filters.max_time_percentage}% Zeit<br>`;
          container.innerHTML += `Max. +${filters.max_additional_minutes} Min<br>`;
          if (filters.enforce_metric_constraints) {
            container.innerHTML += `Min. ${filters.metric_improvement_percentage} % Verbesserung/Verschlechterung<br>`;
          }
          if (filters.max_legs !== undefined) {
            container.innerHTML += `Max. ${filters.max_legs} Umstiege<br>`;
          }
          container.innerHTML += `</div>`;

          // Status indicators
          container.innerHTML += `
            <div class="legend-item" style="display: flex; align-items: center; margin: 4px 0;">
              <span style="color: #4caf50;">✅</span>
              <span style="margin-left: 5px; font-size: 12px;">Alternative gefunden</span>
            </div>
            <div class="legend-item" style="display: flex; align-items: center; margin: 4px 0;">
              <span style="color: #ff9800;">⚠️</span>
              <span style="margin-left: 5px; font-size: 12px;">Keine Alternative</span>
            </div>
          `;

          // Use same transport types as BestTransport
          const transportTypes = [
            { name: "Fußweg", icon: "directions_walk", configKey: "Fuss_Weg" },
            { name: "Fahrrad", icon: "directions_bike", configKey: "Fahrrad" },
            { name: "Pedelec", icon: "electric_bike", configKey: "Pedelec" },
            { name: "ÖV", icon: "directions_bus", configKey: "OeV" },
            {
              name: "Bike+Ride",
              icon: "directions_bike",
              configKey: "Bike_and_Ride",
            },
            { name: "Pkw", icon: "directions_car", configKey: "PKW" },
            { name: "E-Pkw", icon: "electric_car", configKey: "E_Auto" },
            { name: "Park+Ride", icon: "commute", configKey: "Park_and_Ride" },
            {
              name: "Werksbus",
              icon: "airport_shuttle",
              configKey: "Werksbus",
            },
            {
              name: "Bike+Werksbus",
              icon: "airport_shuttle",
              configKey: "Bike_and_Werksbus",
            },
            {
              name: "Bahnreaktivierung",
              icon: "directions_bus",
              configKey: "Bahnreakt_Ride",
            },
            {
              name: "Park+Ride+Bike",
              icon: "commute",
              configKey: "Park_Ride_and_Bike",
            },
          ];

          transportTypes.forEach((transport) => {
            const color = materialIcon.fillColor[transport.configKey];
            if (transport.name === "Bahnreaktivierung") {
              // Train replacement service with composite icon
              const brColor =
                materialIcon.fillColor[transport.configKey] || "#10DADE";
              container.innerHTML += `
                <div class="legend-item" style="position: relative; display: flex; align-items: center; margin: 2px 0;">
                  <span class="icon-combo">
                    <span class="material-icons base" style="color: ${brColor};">directions_bus</span>
                    <span class="material-icons overlay" style="color: ${brColor};">train</span>
                  </span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            } else if (transport.name === "Park+Ride+Bike") {
              // Composite: commute base (depicts car + train) + bike overlay.
              // display:flex statt inline-flex, sonst rutscht der Eintrag neben
              // den vorhergehenden Bahnreaktivierung-Eintrag in dieselbe Zeile.
              const parkRideBikeColor =
                materialIcon.fillColor[transport.configKey] || "#ffd000";
              container.innerHTML += `
                <div class="legend-item" style="position: relative; display: flex; align-items: center; margin: 2px 0;">
                  <span class="icon-combo">
                    <span class="material-icons base" style="color: ${parkRideBikeColor};">commute</span>
                    <span class="material-icons overlay" style="color: ${parkRideBikeColor};">directions_bike</span>
                  </span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            } else if (transport.name === "Bike+Ride") {
              // Force Bike+Ride color to #025b29 with combined icon
              const bikeRideColor =
                materialIcon.fillColor[transport.configKey] || "#025b29";
              container.innerHTML += `
                <div class="legend-item" style="position: relative; display: inline-flex; align-items: center;">
                  <span class="icon-combo">
                    <span class="material-icons base" style="color: ${bikeRideColor};">directions_bus</span>
                    <span class="material-icons overlay" style="color: ${bikeRideColor};">directions_bike</span>
                  </span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            } else if (transport.name === "Bike+Werksbus") {
              // Farbe aus iconConfig, damit Legende und Marker nicht auseinanderlaufen
              const bikeWerksbusColor =
                materialIcon.fillColor[transport.configKey] || "#5b6480";
              container.innerHTML += `
                <div class="legend-item" style="position: relative; display: flex; align-items: center; margin: 2px 0;">
                  <span class="icon-combo">
                    <span class="material-icons base" style="color: ${bikeWerksbusColor};">airport_shuttle</span>
                    <span class="material-icons overlay" style="color: ${bikeWerksbusColor};">directions_bike</span>
                  </span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            } else if (
              transport.configKey === "Werksbus" &&
              color.includes("gradient")
            ) {
              // Handle Werksbus gradient color with circular background
              container.innerHTML += `
                <div class="legend-item" style="display: inline-flex; align-items: center;">
                  <div class="legend-gradient-icon" style="
                    background: ${color};
                    border: 2px solid #CC5500;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-right: 5px;
                  ">
                    <span class="material-icons" style="font-size: 16px; color: white;">${transport.icon}</span>
                  </div>
                  <span style="vertical-align: middle;">${transport.name}</span>
                </div>
              `;
            } else {
              container.innerHTML += `
                <div class="legend-item">
                  <span class="material-icons" style="vertical-align: middle; color: ${color};">${transport.icon}</span>
                  <span style="vertical-align: middle; margin-left: 5px;">${transport.name}</span>
                </div>
              `;
            }
          });
        } else if (
          this.lastChosenMap !== undefined &&
          MapType[this.lastChosenMap] === "MultipleAlternatives"
        ) {
          const metricTypeTranslation = {
            time: "Zeit",
            co2: "CO₂-Ausstoß",
            cost: "Kosten",
            sport: "Bewegung",
            distance: "Gesamt-Nutzen",
          };
          const filters = this.mapInputStore.getBestAlternativeFilters;
          const metricLabel =
            metricTypeTranslation[filters.selected_metric] ||
            filters.selected_metric;

          // Header
          container.innerHTML += `<h3>Anzahl Alternativen</h3>`;
          container.innerHTML += `<h3>zu ${legendTransportName(filters.current_transport)}</h3>`;
          container.innerHTML += `<h3>(für ${metricLabel})</h3>`;

          // 1. Filter criteria (directly under header)
          container.innerHTML += `<div style="margin: 5px 0; padding: 4px; background-color: #f5f5f5; border-radius: 4px; font-size: 11px;">`;
          container.innerHTML += `<strong>Kriterien:</strong><br>`;
          container.innerHTML += `Max. +${filters.max_time_percentage}% Zeit<br>`;
          container.innerHTML += `Max. +${filters.max_additional_minutes} Min<br>`;
          if (filters.enforce_metric_constraints) {
            container.innerHTML += `Min. ${filters.metric_improvement_percentage}% Verbesserung<br>`;
          }
          if (filters.max_legs !== undefined) {
            container.innerHTML += `Max. ${filters.max_legs} Umstiege<br>`;
          }
          container.innerHTML += `</div>`;

          // 2. Marker-Erklaerung. Bewusst als Text und nicht als Beispielkreis:
          // ein einzelner Kreis kann weder die Farbskala der Verkehrsmittel
          // noch die wechselnde Zahl zeigen und wurde eher als eigener
          // Markertyp gelesen.
          container.innerHTML += `
            <div style="margin: 6px 0; padding: 5px; background-color: #f5f5f5; border-radius: 4px; font-size: 11px; max-width: 160px;">
              <strong>Marker:</strong><br>
              <div style="margin: 3px 0;">Farbe Kreis = Beste Alternative</div>
              <div style="margin: 3px 0;">Zahl im Kreis = Anzahl von Alternativen</div>
            </div>
          `;

          // "No alternative" marker — uses current transport color
          const currentTransportColorKey: Record<string, string> = {
            Auto: "PKW",
            "E-Auto": "E_Auto",
            ÖV: "OeV",
            Fahrrad: "Fahrrad",
            Pedelec: "Pedelec",
            "Zu Fuß": "Fuss_Weg",
            "Park & Ride": "Park_and_Ride",
            "Bike & Ride": "Bike_and_Ride",
            Werksbus: "Werksbus",
            "Bike & Werksbus": "Bike_and_Werksbus",
            "Bahnreaktivierung & Ride": "Bahnreakt_Ride",
            "Park & Ride & Bike": "Park_Ride_and_Bike",
          };
          const currentColorKey =
            currentTransportColorKey[filters.current_transport] || "PKW";
          const currentColor =
            materialIcon.fillColor[currentColorKey] || "#d96a26";
          // Dieselbe Symbolfarbe wie der Marker, sonst zeigte die Legende
          // eine weisse 0, wo auf der Karte eine graue steht.
          const currentSymbolColor = transportSymbolColor(currentColorKey);

          container.innerHTML += `
            <div class="legend-item" style="display: flex; align-items: center; margin: 4px 0;">
              <div style="
                background-color: ${currentColor};
                border: 2px solid rgba(0,0,0,0.3);
                width: 18px;
                height: 18px;
                border-radius: 50%;
                display: flex;
                justify-content: center;
                align-items: center;
                flex-shrink: 0;
              ">
                <span style="font-size: 11px; font-weight: bold; color: ${currentSymbolColor};">0</span>
              </div>
              <span style="margin-left: 5px; font-size: 12px;">genutztes VM<br>ohne Alternative</span>
            </div>
          `;

          // Transport color legend

          const transportTypes = [
            { name: "Fußweg", icon: "directions_walk", configKey: "Fuss_Weg" },
            { name: "Fahrrad", icon: "directions_bike", configKey: "Fahrrad" },
            { name: "Pedelec", icon: "electric_bike", configKey: "Pedelec" },
            { name: "ÖV", icon: "directions_bus", configKey: "OeV" },
            {
              name: "Bike+Ride",
              icon: "directions_bike",
              configKey: "Bike_and_Ride",
            },
            { name: "Pkw", icon: "directions_car", configKey: "PKW" },
            { name: "E-Pkw", icon: "electric_car", configKey: "E_Auto" },
            { name: "Park+Ride", icon: "commute", configKey: "Park_and_Ride" },
            {
              name: "Werksbus",
              icon: "airport_shuttle",
              configKey: "Werksbus",
            },
            {
              name: "Bike+Werksbus",
              icon: "airport_shuttle",
              configKey: "Bike_and_Werksbus",
            },
            {
              name: "Bahnreaktivierung",
              icon: "directions_bus",
              configKey: "Bahnreakt_Ride",
            },
            {
              name: "Park+Ride+Bike",
              icon: "commute",
              configKey: "Park_Ride_and_Bike",
            },
          ];

          transportTypes.forEach((transport) => {
            const color = materialIcon.fillColor[transport.configKey];
            container.innerHTML += `
              <div class="legend-item" style="display: flex; align-items: center; margin: 2px 0;">
                <div style="
                  background-color: ${color};
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  flex-shrink: 0;
                "></div>
                <span style="margin-left: 5px; font-size: 12px;">${transport.name}</span>
              </div>
            `;
          });
        }

        // Farbskala der PLZ-Dichte. Reine Legende - der Schalter dazu sitzt
        // im linken Menue (TheNavbar), damit die Legende nur erklaert.
        const zipcodeLegendContainer = L.DomUtil.create(
          "div",
          "zipcode-legend-container",
          container,
        );
        zipcodeLegendContainer.style.display = this.zipcodeLegendVisible
          ? "block"
          : "none";
        if (this.zipcodeLegendVisible) {
          this.updateZipcodeLegend(zipcodeLegendContainer);
        }

        return container;
      };
      return legend;
    },
    toggleGeoPackageLayer(layerId: string, visible: boolean) {
      const layer = this.geoPackageLayers.find((l) => l.id === layerId);

      if (layer) {
        layer.visible = visible;

        // Get the Leaflet layer from the store - access additionalLayerGroups directly
        const leafletLayer = this.mapUtilsObj.additionalLayerGroups[layerId];

        if (!leafletLayer) {
          console.warn("No Leaflet layer found for layerId:", layerId);
          return;
        }

        if (visible) {
          // Add layer to map if not already there
          if (!this.mapUtilsObj.mapObject.hasLayer(leafletLayer)) {
            this.mapUtilsObj.mapObject.addLayer(leafletLayer);
          } else {
          }
        } else {
          // Remove from map but keep in additionalLayerGroups
          if (this.mapUtilsObj.mapObject.hasLayer(leafletLayer)) {
            this.mapUtilsObj.mapObject.removeLayer(leafletLayer);
          } else {
          }
        }

        // Update visibility in the geopackageLayers array
        const geoLayer = this.mapUtilsObj.geopackageLayers.find(
          (l) => l.id === layerId,
        );
        if (geoLayer) {
          geoLayer.visible = visible;
        }
      } else {
        console.warn("Layer not found in geoPackageLayers:", layerId);
      }
    },
    removeGeoPackageLayer(layerId: string) {
      const layerIndex = this.geoPackageLayers.findIndex(
        (l) => l.id === layerId,
      );
      if (layerIndex !== -1) {
        this.geoPackageLayers.splice(layerIndex, 1);
        this.mapUtilsObj.removeLayerGroup(layerId);
      }
    },
    updateGeoPackageLayersList() {
      // Use mapUtilsObj.geopackageLayers as the canonical source
      // This ensures we have access to all layer metadata including style
      // Filter to only show layers that were imported through the wizard
      this.geoPackageLayers = this.mapUtilsObj.geopackageLayers
        .filter((layer) => layer.imported === true) // Only show imported layers
        .map((layer) => ({
          id: layer.id,
          name: this.mapUtilsObj.layerDisplayNames[layer.id] || layer.name,
          visible: layer.visible,
          style: layer.style,
          geometryType: layer.geometryType,
        }));
    },
    updateZipcodeLegend(container: HTMLElement) {
      // Clear existing content
      container.innerHTML = "";

      // Get the sorted employee counts
      const zipcodeCounts = Object.values(
        this.employeeCountsByZipcode,
      ) as number[];
      const sortedCounts = zipcodeCounts.sort((a: number, b: number) => b - a);
      const totalZipcodes = sortedCounts.length;

      // Calculate the index ranges for each category
      const darkestCount = Math.round(totalZipcodes * 0.1);
      const darkCount = Math.round(totalZipcodes * 0.25);
      const mediumCount = Math.round(totalZipcodes * 0.25);

      // Get the range values for each category
      const darkestRange = [sortedCounts[darkestCount - 1], sortedCounts[0]];
      const darkRange = [
        sortedCounts[darkCount + darkestCount - 1],
        sortedCounts[darkestCount],
      ];
      const mediumRange = [
        sortedCounts[mediumCount + darkCount + darkestCount - 1],
        sortedCounts[darkCount + darkestCount],
      ];
      const lightRange = [
        sortedCounts[totalZipcodes - 1],
        sortedCounts[mediumCount + darkCount + darkestCount],
      ];

      // Add zipcode density legend
      container.innerHTML = "<h4>Mitarbeiter je PLZ</h4>";
      container.innerHTML += `<i style="background: #8B4513"></i> ${darkestRange[0]} - ${darkestRange[1]} (Höchste Dichte)<br>`;
      container.innerHTML += `<i style="background: #A0522D"></i> ${darkRange[0]} - ${darkRange[1]} (Hohe Dichte)<br>`;
      container.innerHTML += `<i style="background: #CD853F"></i> ${mediumRange[0]} - ${mediumRange[1]} (Mittlere Dichte)<br>`;
      container.innerHTML += `<i style="background: #DEB887"></i> ${lightRange[0]} - ${lightRange[1]} (Niedrige Dichte)<br>`;
    },
    async updateGeoPackageLayerStyle(layerId: string, style: any) {
      // Forward to the mapStore
      await this.mapUtilsObj.updateGeoPackageLayerStyle(layerId, style);

      // Update our local list
      this.updateGeoPackageLayersList();
    },
    async createZipcodeLayer(
      zipcodeData: GeoJSON.FeatureCollection,
      shouldHide: boolean = true,
    ) {
      // Store the original data for later re-simplification
      this.originalZipcodeData = zipcodeData;

      this.calculateEmployeeCounts();
      const colorCategories = this.calculateColorCategories();

      // Get current zoom level for appropriate simplification
      const currentZoom = this.mapUtilsObj.mapObject?.getZoom() || 10;
      this.lastSimplifiedZoom = Math.floor(currentZoom);

      // Check if we already have a simplified version for this zoom level
      if (this.simplifiedZipcodeData[this.lastSimplifiedZoom]) {
        zipcodeData = this.simplifiedZipcodeData[this.lastSimplifiedZoom];
      } else {
        // Apply the simplification
        const simplifiedData = simplifyGeoJson(zipcodeData, currentZoom);

        // Cache the result
        this.simplifiedZipcodeData[currentZoom] = simplifiedData;
        zipcodeData = simplifiedData;
      }

      this.zipcodeColorCategories = colorCategories;

      this.zipcodeLayer = L.geoJSON(zipcodeData, {
        style: (feature) => this.zipcodeStyle(feature),
        onEachFeature: (feature, layer) => {
          // Im Bearbeitungsmodus waehlt ein Klick das Gebiet aus.
          layer.on("click", () => {
            if (!this.surchargeEditMode) return;
            const plz = feature.properties?.plz;
            if (plz) this.togglePlzSelection(String(plz));
          });

          if (feature.properties) {
            layer.bindPopup(this.zipcodePopupHtml(feature));
          }
        },
      });
      // Der Layer wird auch beim Zoomen neu aufgebaut (resimplifyZipcodeLayer).
      // Deshalb muss der Aufbau selbst den Bearbeitungsmodus beruecksichtigen -
      // sonst kaemen die Popups nach jedem Zoom zurueck.
      this.setZipcodePopupsEnabled(!this.surchargeEditMode);

      if (shouldHide) {
        await this.toggleZipCodeLayer(false); // Initially hide the layer
      }
    },
    /** Inhalt des Info-Popups eines PLZ-Gebiets. */
    zipcodePopupHtml(feature: any): string {
      const { plz, note, einwohner, qkm } = feature.properties;
      const employeeCount = this.getEmployeeCountForZipcode(plz);
      const density = (employeeCount / qkm).toFixed(2);
      const employeeRatio = ((employeeCount / einwohner) * 100).toFixed(2);
      return `
              <h4>Postleitzahl: ${plz}</h4>
              <p>Beschreibung: ${note}</p>
              <p>Einwohnerzahl: ${einwohner}</p>
              <p>Fläche (in km²): ${qkm} km²</p>
              <p>Mitarbeiter: ${employeeCount}</p>
              <p>Mitarbeiterdichte: ${density} pro km²</p>
              <p>Mitarbeiter/Einwohner Verhältnis: ${employeeRatio} %</p>
              <p>Pendler: Datenquelle fehlt noch!</p>
            `;
    },
    /**
     * Info-Popups der PLZ-Gebiete an- oder abschalten.
     *
     * Im Bearbeitungsmodus stoeren sie: Leaflet oeffnet das Popup nach dem
     * eigenen Klick-Handler, ein closePopup() im Handler kommt also zu frueh.
     * Deshalb werden sie fuer die Dauer der Bearbeitung ganz abgehaengt -
     * sonst muesste man bei jeder der rund 100 PLZ ein Infofenster wegklicken.
     */
    setZipcodePopupsEnabled(enabled: boolean) {
      this.zipcodeLayer?.eachLayer((layer: any) => {
        if (enabled) {
          if (layer.feature?.properties && !layer.getPopup()) {
            layer.bindPopup(this.zipcodePopupHtml(layer.feature));
          }
        } else {
          layer.closePopup();
          layer.unbindPopup();
        }
      });
    },
    /**
     * Darstellung eines PLZ-Gebiets.
     *
     * Drei Zustände, in dieser Reihenfolge:
     *  1. ausgewählt (nur im Bearbeitungsmodus) - kräftiger Rand, damit die
     *     Auswahl auch auf gefüllten Flächen erkennbar bleibt
     *  2. Bearbeitungsmodus - Einfärbung nach Aufschlagshöhe
     *  3. sonst - die gewohnte Einfärbung nach Mitarbeiterdichte
     */
    zipcodeStyle(feature: any): L.PathOptions {
      const plz = String(feature?.properties?.plz ?? "");

      if (this.surchargeEditMode) {
        // Set und Aufschlagstabelle werden je Durchlauf einmal aufgebaut
        // (siehe refreshZipcodeStyles). Ohne das waere die Suche in der
        // Auswahl pro Gebiet linear - bei mehreren hundert PLZ also
        // quadratisch, und der Store wuerde je Gebiet erneut geholt.
        const isSelected = this.styleSelectionCache
          ? this.styleSelectionCache.has(plz)
          : this.selectedPlz.includes(plz);
        const maxByPlz =
          this.styleMaxByPlzCache ?? usePlzSurchargeStore().maxByPlz;
        const max = maxByPlz[plz];
        return {
          fillColor: surchargeColor(max?.pct ?? 0, max?.minutes ?? 0),
          weight: isSelected ? 4 : 1,
          opacity: 1,
          color: isSelected ? "#1565c0" : "white",
          fillOpacity: isSelected ? 0.85 : 0.6,
          dashArray: isSelected ? undefined : "3",
        };
      }

      const colorCategory = this.zipcodeColorCategories[plz] || "light";
      return {
        fillColor: this.getColorByCategory(colorCategory),
        weight: 2,
        opacity: 1,
        color: "white",
        fillOpacity: 0.7,
      };
    },
    /** Einfärbung neu anwenden, ohne den Layer neu zu bauen. */
    refreshZipcodeStyles() {
      if (!this.zipcodeLayer) return;
      this.styleSelectionCache = new Set(this.selectedPlz);
      this.styleMaxByPlzCache = usePlzSurchargeStore().maxByPlz;
      this.zipcodeLayer.setStyle((feature: any) => this.zipcodeStyle(feature));
      this.styleSelectionCache = null;
      this.styleMaxByPlzCache = null;
    },
    /**
     * Nur ein einzelnes Gebiet neu einfaerben.
     *
     * Ein Klick aendert genau ein Polygon. setStyle() ueber den ganzen Layer
     * zeichnet dagegen alle Gebiete neu - bei echten PLZ-Grenzen mit tausenden
     * Stuetzpunkten je Flaeche ist das bei jedem Klick spuerbar.
     */
    restyleSinglePlz(plz: string) {
      this.zipcodeLayer?.eachLayer((layer: any) => {
        if (String(layer.feature?.properties?.plz ?? "") !== plz) return;
        layer.setStyle(this.zipcodeStyle(layer.feature));
      });
    },
    /**
     * Bearbeitungsmodus für Stauaufschläge ein- oder ausschalten.
     *
     * Der PLZ-Layer wird dabei zwingend eingeblendet - ohne sichtbare Gebiete
     * ließe sich nichts auswählen.
     */
    async toggleSurchargeEditMode(enabled: boolean) {
      this.surchargeEditMode = enabled;

      if (enabled) {
        // Ein Fehler beim Laden der Verkehrsmittel darf den Modus nicht
        // blockieren; der Dialog zeigt dann eine leere Liste.
        try {
          await usePlzSurchargeStore().fetchTransports();
        } catch {
          /* Liste bleibt leer */
        }
        // Sichtbarkeit merken: der Bearbeitungsmodus blendet die Gebiete
        // zwingend ein (ohne sie liesse sich nichts auswaehlen), beim
        // Verlassen muss aber wieder der vorherige Zustand gelten. Sonst
        // bleiben die Gebiete nach dem Schliessen in der Dichte-Einfaerbung
        // stehen, obwohl der Anwender sie nie eingeschaltet hat.
        this.zipcodeVisibleBeforeEdit = this.zipcodeLegendVisible;
        if (!this.zipcodeLegendVisible) {
          await this.setZipcodeLayerVisible(true);
        }
      } else {
        this.stopPolygonDraw();
        this.selectedPlz = [];
        // Immer aufrufen, auch wenn der Zustand schon zu stimmen scheint:
        // die Legende kann durch das erzwungene Einblenden aus dem Tritt
        // geraten sein und wird hier mit angeglichen.
        await this.setZipcodeLayerVisible(this.zipcodeVisibleBeforeEdit);
      }
      // Popups nur ausserhalb der Bearbeitung
      this.setZipcodePopupsEnabled(!enabled);
      this.refreshZipcodeStyles();
    },
    /**
     * PLZ-Gebiete auf Wunsch des Anwenders ein- oder ausblenden.
     *
     * Waehrend der Bearbeitung sind die Gebiete erzwungen sichtbar; schaltet
     * der Anwender dort bewusst um, ist das der neue Sollzustand, den das
     * Verlassen der Bearbeitung nicht ueberschreiben darf.
     */
    async onZipcodeToggle(visible: boolean) {
      await this.setZipcodeLayerVisible(visible);
      if (this.surchargeEditMode) {
        this.zipcodeVisibleBeforeEdit = this.zipcodeLegendVisible;
      }
    },
    /**
     * PLZ-Layer ein- oder ausblenden und die Legende mitfuehren.
     *
     * Die Farbskala in der Legende wird imperativ aufgebaut und kennt den
     * Layerzustand nicht von selbst. Wird der Layer aus dem Code heraus
     * geschaltet - beim Betreten und Verlassen der Bearbeitung -, muss sie
     * mitgezogen werden, sonst steht die Skala noch da, waehrend die Gebiete
     * schon weg sind.
     */
    async setZipcodeLayerVisible(visible: boolean) {
      await this.toggleZipCodeLayer(visible);
      this.syncZipcodeLegendControls();
    },
    /**
     * Farbskala der PLZ-Dichte an den tatsaechlichen Layerzustand angleichen.
     *
     * Bewusst aus this.zipcodeLegendVisible gelesen und nicht aus dem zuvor
     * gewuenschten Wert: toggleZipCodeLayer kann das Einblenden verweigern,
     * wenn der Layer noch gar nicht aufgebaut ist. Das Haekchen im linken
     * Menue haengt an derselben Eigenschaft und zieht dadurch von selbst mit.
     */
    syncZipcodeLegendControls() {
      const visible = this.zipcodeLegendVisible;

      const legendContainer = document.querySelector(
        ".zipcode-legend-container",
      ) as HTMLElement | null;
      if (legendContainer) {
        legendContainer.style.display = visible ? "block" : "none";
        if (visible) this.updateZipcodeLegend(legendContainer);
      }
    },
    /** Ein Gebiet zur Auswahl hinzufügen oder daraus entfernen. */
    togglePlzSelection(plz: string) {
      this.selectedPlz = [...togglePlz(new Set(this.selectedPlz), plz)].sort();
      this.restyleSinglePlz(plz);
    },
    clearPlzSelection() {
      this.selectedPlz = [];
      this.refreshZipcodeStyles();
    },
    /**
     * Polygon-Werkzeug starten.
     *
     * Nach dem Zeichnen wird das Polygon sofort wieder entfernt: es ist reines
     * Auswahlwerkzeug und soll nicht als Grafik auf der Karte liegen bleiben.
     * Die Auflösung zu Postleitzahlen passiert im Browser auf den ohnehin
     * geladenen Gebieten - kein Serveraufruf.
     */
    startPolygonDraw() {
      const map = this.mapUtilsObj.mapObject as any;
      if (!map?.pm) return;

      // Handler erst beim ersten Zeichnen registrieren: die Karte entsteht
      // asynchron in TheMap.vue und meldet ihre Bereitschaft nicht. Das Flag
      // verhindert Mehrfachregistrierung.
      if (!this.polygonHandlerBound) {
        map.on("pm:create", (event: any) => {
          const drawn = event?.layer;
          if (!drawn) return;
          let geoJson: any = null;
          try {
            geoJson = drawn.toGeoJSON();
          } finally {
            // Das Polygon ist Auswahlwerkzeug, kein Kartenobjekt - es darf
            // nicht als Grafik liegen bleiben.
            map.removeLayer(drawn);
          }
          if (geoJson) this.handleDrawnPolygon(geoJson);
        });
        this.polygonHandlerBound = true;
      }

      this.polygonDrawActive = true;
      map.pm.enableDraw("Polygon", { snappable: false, finishOn: "dblclick" });
    },
    stopPolygonDraw() {
      const map = this.mapUtilsObj.mapObject as any;
      if (!map?.pm) return;
      this.polygonDrawActive = false;
      map.pm.disableDraw();
    },
    /** Treffer eines gezeichneten Polygons in die Auswahl übernehmen. */
    handleDrawnPolygon(geoJson: any) {
      const data =
        this.originalZipcodeData ?? this.mapOutputStore.getZipcodeLayerData;
      const hits = plzTouchedByPolygon(data, geoJson);
      if (hits.length > 0) {
        this.selectedPlz = [...addPlz(new Set(this.selectedPlz), hits)].sort();
        this.refreshZipcodeStyles();
      }
      this.polygonDrawActive = false;
    },
    /** Aufschlag für die aktuelle Auswahl speichern. */
    async saveSurchargeForSelection(payload: {
      plzList: string[];
      values: {
        transport_type: string;
        surcharge_pct: number;
        surcharge_minutes: number;
      }[];
    }) {
      const officeId = useOfficeStore().selectedOffice?.office_id;
      if (!officeId) return;

      this.surchargeSaving = true;
      try {
        await usePlzSurchargeStore().applyToPlzList(
          officeId,
          payload.plzList,
          payload.values,
        );
        this.surchargeDialogOpen = false;
        this.selectedPlz = [];
        this.refreshZipcodeStyles();

        // Der Zaehler entwertet den Cache der Layer-Getter. Das ist billig -
        // neu gerechnet wird erst, wenn ein Layer wirklich abgerufen wird.
        useMapOutputStore().surchargeVersion++;

        // Die Marker nur neu aufbauen, wenn der Aufschlag auch angezeigt wird.
        // Beim Pflegen ist der Schalter typischerweise aus, und ein Neuaufbau
        // kostet je Standort mehrere tausend Marker samt Metrik-Abruf - fuer
        // ein sichtbar identisches Ergebnis. Die Legende zeigt nur die
        // Mitarbeiterdichte und aendert sich durch gespeicherte Aufschlaege
        // nicht; der Schalter im Menue folgt dem Store von selbst.
        if (usePlzSurchargeStore().enabled) {
          this.changedSettingsHandler();
        }
      } catch (error) {
        console.error("Error saving PLZ surcharges:", error);
        this.surchargeSaveError =
          usePlzSurchargeStore().error ?? "Speichern fehlgeschlagen";
      } finally {
        this.surchargeSaving = false;
      }
    },
    // Add this method to TheMapEditor.vue
    async resimplifyZipcodeLayer(zoomLevel: number) {
      if (!this.zipcodeLayer || !this.zipcodeLegendVisible) {
        return;
      }

      // Get the original data
      const zipcodeLayerData = this.mapOutputStore.getZipcodeLayerData;
      if (!zipcodeLayerData) {
        console.warn("No zipcode data available to resimplify");
        return;
      }

      // Check if we need to resimplify (only do it when zoom changes significantly)
      const roundedZoom = Math.floor(zoomLevel);
      if (this.lastSimplifiedZoom === roundedZoom) {
        return;
      }

      // Apply simplification with the new zoom level
      const simplifiedData = simplifyGeoJson(zipcodeLayerData, zoomLevel);

      // Remove existing layer
      this.mapUtilsObj.removeLayerGroup("zipcode");

      // Store current zoom level for future reference
      this.lastSimplifiedZoom = roundedZoom;

      // Recreate the layer with simplified data (don't hide it since we want to keep it visible)
      await this.createZipcodeLayer(simplifiedData, false);

      // Show the layer since we're resimplifying an already visible layer
      await this.toggleZipCodeLayer(true);
    },
    getColorByCategory(category: string): string {
      switch (category) {
        case "darkest":
          return "#8B4513"; // Deepest brown
        case "dark":
          return "#A0522D"; // Second deepest
        case "medium":
          return "#CD853F"; // Third deepest
        default:
          return "#DEB887"; // Lightest brown
      }
    },
    async toggleZipCodeLayer(isVisible: boolean) {
      if (isVisible && this.zipcodeLayer) {
        this.zipcodeLegendVisible = true;
        await this.mapUtilsObj.addLayerGroup(
          "zipcode",
          this.zipcodeLayer,
          undefined,
          undefined,
          false,
        );
      } else {
        this.zipcodeLegendVisible = false;
        this.mapUtilsObj.removeLayerGroup("zipcode");
      }
    },
    async renderGeoJsonInChunks(geoJsonLayer, map, chunkSize = 10) {
      // If the layer is already added to the map, do nothing
      if (map.hasLayer(geoJsonLayer)) {
        return;
      }

      // Get all sublayers
      const sublayers = [];
      geoJsonLayer.eachLayer((layer) => {
        sublayers.push(layer);
      });

      // Process in chunks
      const totalChunks = Math.ceil(sublayers.length / chunkSize);

      for (let i = 0; i < sublayers.length; i += chunkSize) {
        const chunk = sublayers.slice(i, i + chunkSize);
        const tempLayer = L.layerGroup(chunk);

        // Add this chunk to the map
        tempLayer.addTo(map);

        // Allow UI to update before processing next chunk
        if (i + chunkSize < sublayers.length) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      return true;
    },
    // async captureMap() {
    //   const mapElement = document.getElementById("elementId");
    //   if (!mapElement) {
    //     console.error("Map container not found");
    //     return;
    //   }

    //   const map = this.$refs.jmMap.leafletObject;

    //   // Wait for any ongoing animations to complete
    //   await new Promise((resolve) => {
    //     if (map.isZooming() || map.isPanning()) {
    //       map.once("moveend", resolve);
    //     } else {
    //       resolve();
    //     }
    //   });

    //   // Capture the entire map container
    //   const canvas = await html2canvas(mapElement, {
    //     useCORS: true,
    //     allowTaint: true,
    //     logging: true,
    //     ignoreElements: (element) => {
    //       // Ignore any elements that shouldn't be in the screenshot
    //       return element.classList.contains("leaflet-control-container");
    //     },
    //   });

    //   // Convert to blob and download
    //   canvas.toBlob((blob) => {
    //     if (!blob) {
    //       console.error("Failed to create blob from canvas");
    //       return;
    //     }

    //     const a = document.createElement("a");
    //     document.body.appendChild(a);
    //     a.style.display = "none";
    //     const url = window.URL.createObjectURL(blob);
    //     a.href = url;
    //     a.download = "map-screenshot.png";
    //     a.click();
    //     window.URL.revokeObjectURL(url);
    //     document.body.removeChild(a);
    //   });
    // },
  },
});
</script>
<style>
.icon-combo {
  position: relative;
  display: inline-block;
  width: 24px;
  height: 24px;
}
.icon-combo .base {
  font-size: 22px;
  line-height: 24px;
}
.icon-combo .overlay {
  position: absolute;
  right: -6px;
  bottom: -6px;
  font-size: 16px;
}

/* Gradient legend icon styling for transports like Werksbus */
.legend-gradient-icon {
  flex-shrink: 0; /* Prevent shrinking */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2); /* Subtle shadow for depth */
}

.legend-gradient-icon .material-icons {
  line-height: 1; /* Ensure proper vertical centering */
}

.leaflet-control .zipcode-checkbox {
  margin-right: 5px;
}
.leaflet-popup-content {
  font-family: Arial, sans-serif;
}
.leaflet-popup-content h4 {
  margin: 0 0 10px 0;
  color: #333;
}
.leaflet-popup-content p {
  margin: 5px 0;
  color: #666;
}
.leaflet-control .zipcode-checkbox {
  margin-right: 5px;
  border-radius: 50%;
}
/* Bedienfeld der PLZ-Auswahl. Links unten, damit es weder die Legende
   (rechts) noch die Zoom-Bedienung (links oben) verdeckt. */
.surcharge-panel {
  position: absolute;
  bottom: 24px;
  left: 70px;
  z-index: 1000;
  width: 260px;
}
.surcharge-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  border: 1px solid #999;
  border-radius: 2px;
}
.leaflet-tooltip.no-arrow {
  background-color: rgba(255, 255, 255, 0.7);
  border: 1px solid #777;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  color: #333;
  padding: 4px 8px;
}

.leaflet-tooltip.no-arrow::before {
  display: none;
}
</style>
